import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Tab, TabsComponent } from 'components/tabs/tabs.component';
import { TableComponent } from 'components/table/table.component';
import { API_CLIENT, API_LEGAL_ENTITY, ApiService } from 'services/api.service';
import { ClientStatus } from 'types/client.interface';
import { FilterType, TableColumn } from 'types/table-column.interface';

@Component({
  selector: 'app-client-list-page',
  standalone: true,
  imports: [TableComponent, TabsComponent, TranslateModule, NgClass],
  templateUrl: './client-list-page.component.html',
  styleUrl: './client-list-page.component.scss'
})
export class ClientListPageComponent implements OnInit {
  public readonly clientApiPath = API_CLIENT;
  public readonly legalEntityApiPath = API_LEGAL_ENTITY;
  isExcelDownloading: boolean = false;
  activeTab = 'individual';

  tabs: Tab[] = [
    { key: 'individual', label: 'pages.client.individualTab' },
    { key: 'organization', label: 'pages.client.organizationTab' }
  ];

  individualDefaultFilters: { [key: string]: string } = { clientType: 'Individual' };

  individualTableColumns: TableColumn[] = [
    {
      name: 'pages.client.tableColumns.name',
      key: 'fullName',
      sortEnabled: true,
      sortKey: 'fullName',
      styleClass: 'client-name',
      filter: FilterType.Search,
      conditionalRenderKey: 'hasReaderCard'
    },
    {
      name: 'pages.client.tableColumns.identifier',
      key: 'nationalDigitalId',
      sortKey: 'nationalDigitalId',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.officialDocumentId',
      key: 'officialDocumentId',
      sortKey: 'officialDocumentId',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.birthday',
      key: 'birthday',
      sortKey: 'birthday',
      sortEnabled: true,
      customLogic: 'birthday',
      filter: FilterType.Date,
      type: 'date'
    },
    {
      name: 'pages.client.tableColumns.country',
      key: 'country.name',
      sortKey: 'country',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.email',
      key: 'email',
      sortKey: 'email',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.phone',
      key: 'phone',
      sortKey: 'phone',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.createdAt',
      key: 'createdOn',
      sortEnabled: true,
      sortKey: 'createdOn',
      type: 'date',
      filter: FilterType.DateTime
    },
    {
      name: 'pages.client.tableColumns.lastSession',
      key: 'loggedOn',
      sortEnabled: true,
      sortKey: 'loggedOn',
      type: 'date',
      filter: FilterType.DateTime
    },
    {
      name: 'pages.client.tableColumns.status',
      key: 'status',
      sortEnabled: true,
      multiselectFilterName: 'statuses',
      styleClass: 'status-column',
      filter: FilterType.MultiSelect,
      sortKey: 'status',
      filterOptions: Object.values(ClientStatus),
      valueTranslate: 'pages.client.FrontofficeReadingRoomSubmissionStatus.'
    },
    {
      name: 'pages.client.tableColumns.authType',
      key: 'authenticationType',
      multiselectFilterName: 'authenticationTypes',
      sortEnabled: true,
      sortKey: 'authenticationType',
      filter: FilterType.MultiSelect,
      filterOptions: ['Local', 'NationalId', 'Email', 'Unknown'],
      valueTranslate: 'pages.client.FrontofficeReadingRoomSubmissionStatus.'
    }
  ];

  organizationTableColumns: TableColumn[] = [
    {
      name: 'pages.client.tableColumns.organizationName',
      key: 'name',
      sortEnabled: true,
      sortKey: 'name',
      filter: FilterType.Search,
      customLogic: 'orgNameWithType'
    },
    {
      name: 'pages.client.tableColumns.registrationNumber',
      key: 'registrationNumber',
      sortKey: 'registrationNumber',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.registrationCountry',
      key: 'country.name',
      sortKey: 'country',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.email',
      key: 'email',
      sortKey: 'email',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.phone',
      key: 'phone',
      sortKey: 'phone',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.client.tableColumns.createdAt',
      key: 'createdOn',
      sortEnabled: true,
      sortKey: 'createdOn',
      type: 'date',
      filter: FilterType.DateTime
    },
    {
      name: 'pages.client.tableColumns.authType',
      key: 'authenticationType',
      multiselectFilterName: 'authenticationTypes',
      sortEnabled: true,
      sortKey: 'authenticationType',
      filter: FilterType.MultiSelect,
      filterOptions: ['Local', 'NationalId'],
      valueTranslate: 'pages.client.FrontofficeReadingRoomSubmissionStatus.'
    }
  ];

  constructor(
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.activeTab = this.route.snapshot.data['tab'] === 'organization' ? 'organization' : 'individual';
  }

  setActiveTab(tab: string): void {
    if (tab === 'organization') {
      this.router.navigate(['/klienti/juridiskas-personas']);
    } else {
      this.router.navigate(['/klienti']);
    }
  }

  getExcelFile() {
    if (!this.isExcelDownloading) {
      this.isExcelDownloading = true;
      const excel$ = this.activeTab === 'organization'
        ? this.apiService.getOrganizationExcel()
        : this.apiService.getClientExcel();
      excel$.subscribe(
        (data: any) => {
          if (data) {
            const filename = this.activeTab === 'organization' ? 'organizacijas.xlsx' : 'klienti.xlsx';
            this.saveExcelFile(data, filename);
            this.isExcelDownloading = false;
          }
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }

  saveExcelFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  navigateToAddPage(): void {
    if (this.activeTab === 'organization') {
      this.router.navigate(['/klienti/pievienot-juridisku-personu']);
    } else {
      this.router.navigate(['/klienti/pievienot']);
    }
  }
}
